import { type CSSInterpolation } from '@emotion/serialize'
import PropTypes from 'prop-types'
import React from 'react'

import BusyOverlay from 'components/common/BusyOverlay'
import Text from 'components/common/Text'
import log from 'log'
import theme from 'theme'

// <div> with a css prop
type LoadingBoxProps = React.HTMLAttributes<HTMLDivElement> & {
  css: CSSInterpolation
}

type SectionProps = {
  children: React.ReactNode
  isLoading?: boolean
  hasError?: boolean
  errorMessage?: React.ReactNode
  darkTheme?: boolean
  renderError?: () => React.ReactElement
  loadingBoxProps?: LoadingBoxProps
}

type SectionState = { hasError: boolean }

export default class Section extends React.Component<
  SectionProps,
  SectionState
> {
  static getDerivedStateFromError(): SectionState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true }
  }

  componentDidCatch(error: Error): void {
    log.error(error)
  }

  state = {
    hasError: false
  }

  render(): JSX.Element {
    const {
      hasError,
      errorMessage,
      isLoading,
      children,
      darkTheme = false,
      renderError = null,
      loadingBoxProps = {} as LoadingBoxProps,
      ...rest
    } = this.props

    if (hasError || this.state.hasError) {
      return renderError ? (
        renderError()
      ) : (
        <ErrorSection darkTheme={darkTheme} message={errorMessage} />
      )
    }

    const { css: loadingBoxCss, ...loadingBoxRest } = loadingBoxProps

    return (
      <section
        css={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1
        }}
        {...rest}
      >
        {isLoading ? (
          <div
            css={[{ position: 'relative', minHeight: 256 }, loadingBoxCss]}
            role="progressbar"
            {...loadingBoxRest}
          >
            <BusyOverlay shown transparentBg={darkTheme} />
          </div>
        ) : (
          <>{typeof children === 'function' ? children() : children}</>
        )}
      </section>
    )
  }
}

function ErrorSection({ message, darkTheme, ...rest }) {
  return (
    <div
      data-test-error-section
      css={{
        paddingTop: theme.space[5],
        paddingRight: 0,
        paddingBottom: theme.space[5],
        paddingLeft: 0
      }}
      {...rest}
    >
      <Text as="h5" color={darkTheme ? 'white' : undefined}>
        {message}
      </Text>
    </div>
  )
}

ErrorSection.propTypes = {
  darkTheme: PropTypes.bool,
  message: PropTypes.string
}

ErrorSection.defaultProps = {
  message:
    "We've encountered an error with this content. Please try again later."
}
